import { AdminPageHeader } from "@/components/admin-page-header";
import { getOpportunityById, type OpportunityDto } from "@fp/admin-api-client";
import { Button } from "@fp/ui/components/base/buttons/button";
import {
	PageContentContainer,
	PageLoading,
	PageProgressBar,
} from "@fp/ui/components/base/page-layout/page-layout";
import { AdminLayout } from "@/components/admin-layout";
import {
	ProofOfWorkForm,
	ProofOfWorkFormContent,
	ProofOfWorkFormErrorMessage,
	type ProofOfWorkFormContext,
	toReferenceRequirementFormValues,
} from "@fp/ui/components/opportunities/opportunity-proof-of-work-form";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

interface ProofOfWorkPageProps {
	opportunity: OpportunityDto;
	onSuccess: (opportunity: OpportunityDto) => Promise<void>;
}

export const ProofOfWorkPageContent = ({
	opportunity,
	onSuccess,
}: ProofOfWorkPageProps) => {
	const defaultValues = useMemo(() => {
		const requireImage = opportunity.requireImage || false;
		const requireDescription = opportunity.requireDescription || false;
		const requireReferenceId = opportunity.referenceRequirements.length > 0;
		const hasAnyProofOfWorkItems =
			requireImage || requireDescription || requireReferenceId;
		return {
			id: opportunity.id,
			requireProofOfWork: hasAnyProofOfWorkItems,
			requireImage,
			requireDescription,
			requireReferenceId,
			referenceRequirements: toReferenceRequirementFormValues(opportunity.referenceRequirements),
			instructions: opportunity.payoutInstructions || "",
		};
	}, [opportunity]);

	return (
		<ProofOfWorkForm defaultValues={defaultValues} onSuccess={onSuccess}>
			{({ handleSubmit, transition }: ProofOfWorkFormContext) => (
				<>
					<p className="text-md text-quaternary">
						If you'd like your team to submit proof when they request payout,
						enable here.{" "}
					</p>

					<ProofOfWorkFormContent />
					<ProofOfWorkFormErrorMessage />

					<div className="flex-1 flex items-end pt-6">
						<Button
							isDisabled={transition}
							type="button-primary"
							onClick={handleSubmit}
							className="h-16 w-full"
						>
							Preview your Opp
						</Button>
					</div>
				</>
			)}
		</ProofOfWorkForm>
	);
};

export const Route = createFileRoute("/opps/$oppId/edit/proof-of-work")({
	component: RouteComponent,
	loader: async ({ params }) => {
		const { oppId } = params;
		const result = await getOpportunityById(oppId);

		return result.data;
	},
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const opportunity = Route.useLoaderData();
	const router = useRouter();
	const { oppId } = Route.useParams();

	const onSuccess = useCallback(
		async (opportunity: OpportunityDto) => {
			await router.invalidate({ sync: true });
			router.navigate({
				to: "/opps/$oppId/edit/detail",
				params: { oppId: opportunity.id },
			});
		},
		[router],
	);

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Proof of Work"
				parent={{
					url: router.buildLocation({
						to: "/opps/$oppId/edit/participation",
						params: { oppId },
					}).href,
					label: "Participation Settings",
				}}
				footer={<PageProgressBar current={3} total={4} />}
			/>
			<PageContentContainer className="space-y-6">
				<ProofOfWorkPageContent
					opportunity={opportunity}
					onSuccess={onSuccess}
				/>
			</PageContentContainer>
		</AdminLayout>
	);
}
